import { intersectionWith } from 'lodash';
import { MNSproCloudApi } from '../..';
import { Holiday } from '../../classbook/Holidays.interfaces';
import {
  holidayResponseToHoliday,
  holidayToHolidayEntry,
  holidayToHolidayResponse,
} from './mappings';

const getApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.ClassRegisterApiFactory(config);
};

export class HolidaysService {
  static async createHoliday(
    holiday: Holiday,
    authToken: string,
    organizationId: string,
  ): Promise<Holiday> {
    const api = getApi(authToken);
    const result = await api.createHoliday(
      organizationId,
      holidayToHolidayEntry(holiday),
    );

    return holidayResponseToHoliday(result.data);
  }
  static async updateHoliday(
    holiday: Holiday,
    authToken: string,
    organizationId: string,
  ): Promise<Holiday> {
    const api = getApi(authToken);
    const result = await api.updateAHoliday(
      organizationId,
      holidayToHolidayResponse(holiday),
    );

    return holidayResponseToHoliday(result.data);
  }
  static async getAllHolidays(
    authToken: string,
    organizationId: string,
  ): Promise<Holiday[]> {
    const api = getApi(authToken);
    const result = await api.getAllHolidays(organizationId);

    return result.data.map(holidayResponseToHoliday);
  }
  static async getLatestHoliday(
    authToken: string,
    organizationId: string,
    holidayId: string,
  ): Promise<Holiday> {
    const api = getApi(authToken);
    const result = await api.getLatestHoliday(holidayId, organizationId);
    return holidayResponseToHoliday(result.data);
  }
  static async deleteHoliday(
    holidayId: string,
    authToken: string,
    organizationId: string,
  ): Promise<void> {
    const api = getApi(authToken);
    api.deletesAHoliday(holidayId, organizationId);
  }

  static async bulkCreateOrUpdate(
    originHolidaysList: Holiday[],
    newList: Holiday[],
    authToken: string,
    organizationId: string,
  ): Promise<Holiday[]> {
    const holidaysToCreate = newList.filter(holiday => !holiday.id);
    for (const holiday of holidaysToCreate) {
      await HolidaysService.createHoliday(holiday, authToken, organizationId);
    }
    const holidaysToUpdate = intersectionWith(
      newList,
      originHolidaysList,
      (a, b) =>
        a.id === b.id &&
        !(a.end === b.end && a.start === b.start && a.name === b.name),
    );

    for (const holiday of holidaysToUpdate) {
      await HolidaysService.updateHoliday(holiday, authToken, organizationId);
    }
    const holidaysToDelete = originHolidaysList
      .map(orig => orig.id)
      .filter(
        origId =>
          origId !== undefined &&
          !newList.map(newHoliday => newHoliday.id).includes(origId),
      );

    for (const holidayToDelete of holidaysToDelete) {
      if (holidayToDelete) {
        await HolidaysService.deleteHoliday(
          holidayToDelete,
          authToken,
          organizationId,
        );
      }
    }

    return await HolidaysService.getAllHolidays(authToken, organizationId);
  }
}
